import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../contexts/AuthContext';
import { DepthChart } from '../../../types/DepthChartTypes';
// Interface for club user
export interface ClubUser {
  id: number;
  name: string;
  phoneNumber?: string;
  email?: string;
}

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  depthChartImage: string | null;
  isGeneratingImage: boolean;
  selectedDepthChart: DepthChart;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  depthChartImage,
  isGeneratingImage,
  selectedDepthChart
}) => {
  const { isAuthenticated } = useAuth();
  const [selectedUsers, setSelectedUsers] = useState<ClubUser[]>([]);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedUsers([]);
    }
  }, [open]);
  
  // Close notification
  const handleCloseNotification = () => {
    setNotification(null);
  };
  
  // Copy image to clipboard
  const copyImageToClipboard = async () => {
    if (!depthChartImage) return;
    
    try {
      // Create a blob from the data URL
      const res = await fetch(depthChartImage);
      const blob = await res.blob();
      
      // Copy to clipboard using the Clipboard API
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      
      setNotification({
        message: 'Image copied to clipboard!',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to copy image to clipboard:', error);
      setNotification({
        message: 'Failed to copy image. Your browser may not support this feature.',
        type: 'error'
      });
    }
  };
  
  // Download image
  const downloadImage = () => {
    if (!depthChartImage) return;
    
    // Create a link element
    const link = document.createElement('a');
    link.href = depthChartImage;
    link.download = `${selectedDepthChart.name}-depth-chart.png`;
    
    // Append to the body, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "white",
            borderRadius: 2,
            width: "100%",
            maxWidth: "800px"
          }
        }}
      >
        <DialogContent sx={{ p: 3, mt: 2, overflow: 'hidden' }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
            {/* Preview section */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography variant="subtitle1" sx={{ mb: 2, alignSelf: "flex-start" }}>
                Preview
              </Typography>
              <Box sx={{ 
                width: "100%", 
                aspectRatio: "16/9",
                bgcolor: "#121212", 
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                position: "relative"
              }}>
                {isGeneratingImage ? (
                  <CircularProgress size={40} sx={{ color: "rgba(123, 45, 58, 0.9)" }} />
                ) : depthChartImage ? (
                  <img 
                    src={depthChartImage} 
                    alt="Depth Chart" 
                    style={{ 
                      maxWidth: "100%", 
                      maxHeight: "100%", 
                      objectFit: "contain" 
                    }} 
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    No preview available
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "row", justifyContent: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={copyImageToClipboard}
                  disabled={!depthChartImage || isGeneratingImage}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    mx: 1
                  }}
                >
                  Copy Image
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={downloadImage}
                  disabled={!depthChartImage || isGeneratingImage}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    mx: 1
                  }}
                >
                  Download Image
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <Button 
            onClick={onClose}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Notification */}
      <Snackbar
        open={!!notification}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type || 'info'}
          variant="filled"
          sx={{ width: '100%', display: notification ? 'flex' : 'none' }}
        >
          {notification?.message || ''}
        </Alert>
      </Snackbar>
    </>
  );
}; 